<template>
  <div id="pointExplorerPage">
    <Navbar
      :tab="'explorer'"
      :siteId="navbarSiteId"
      :zoneId="zoneId"
      :site="site"
      :scoreHelp="scoreHelp"
    />
    <v-container>
      <div class="app-page-breadcrumbs">
        <AppBreadcrumbs label="Point Explorer" :allow-all-sites="true" :site-id="breadcrumbSiteId" />
      </div>
      <v-row class="top-header">
        <v-col class="site-info">
          <div class="site-title">{{ projectName }} Point Explorer</div>
        </v-col>
      </v-row>
      
      <v-row>
        <v-col>
          <PointExplorerFilters
            @search="handleSearch"
            @query-saved="handleQuerySaved"
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col>
          <PointExplorerTable
            :points="filteredPoints"
            :loading="isAnyLoading"
            :pagination="pagination"
            @point-click="handlePointClick"
            @site-click="handleSiteClick"
            @zone-click="handleZoneClick"
            @table-change="handleTableChange"
          />
        </v-col>
      </v-row>
    </v-container>
    
    <!-- Modal for point details -->
    <DashboardAqDetailModal
      :dialog="showDetailModal"
      :measurement="selectedMeasurement"
      @update:dialog="showDetailModal = $event"
      @close="closeDetailModal"
    />
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue';
import PointExplorerFilters from '@/components/pointExplorer/PointExplorerFilters.vue';
import PointExplorerTable from '@/components/pointExplorer/PointExplorerTable.vue';
import DashboardAqDetailModal from '@/components/dashboard/DashboardAqDetailModal.vue';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import { HTTPClient } from '../assets/js/http';
import { firstAccessibleSite, isAccessibleSiteId, sitesUserCanAccess } from '../assets/js/siteAccess';
import moment from 'moment';

let client = new HTTPClient();

export default {
    name: 'PointExplorer',
    components: {
        Navbar,
        PointExplorerFilters,
        PointExplorerTable,
        DashboardAqDetailModal,
        AppBreadcrumbs
    },
    data() {
        return {
            // Site context for navbar
            site: null,
            zoneId: null,
            scoreHelp: null,
            
            // PointExplorer state
            filteredPoints: [],
            loading: false,
            pagination: {
                page: 1,
                limit: 100,
                totalCount: 0,
                totalPages: 0
            },
            
            // Modal state
            showDetailModal: false,
            selectedMeasurement: null,
            
            // Current filter state
            currentFilters: {
                alarmStatus: null,
                pointType: null,
                siteId: null,
                zoneId: null,
                name: null
            },
            
            // Sorting state
            sortBy: 'name',
            sortDesc: false,
            
            // Alarm status management
            pointToMeasurementMap: {}, // pointId → { measurementId, alerts: [], isAlerting: boolean }
            alarmStatusLoaded: false,
            alarmStatusLoading: false,
            
            // Store all points before alarm filtering
            allPoints: [], // All points from API before alarm status filtering
            finalFilteredPoints: [], // Points after alarm status filtering is applied
            
            // Debouncing for performance
            searchDebounceTimer: null,
            
            // Prevent infinite loops in watchers
            loadingSearch: false
        };
    },
    computed: {
        navbarSiteId() {
            return this.$route.query.siteId || this.$route.params.siteId || this.site?.objectId || null;
        },
        /**
         * Site id for the All Sites breadcrumb (query only; default is All Sites).
         * @returns {string|null}
         */
        breadcrumbSiteId() {
            return this.$route.query.siteId || null;
        },
        projectName() {
            // Get current project name from store or route params
            const projectId = this.$route.params.projectId || this.$store.state.project?.objectId;
            if (projectId && this.$store.state.projects.length > 0) {
                const currentProject = this.$store.state.projects.find(p => p.objectId === projectId);
                return currentProject ? currentProject.name : 'Project';
            }
            // Fallback to store project or default
            const project = this.$store.state.project;
            return project ? project.name : 'Project';
        },
        
        isAnyLoading() {
            return this.loading || this.alarmStatusLoading;
        }
    },
    methods: {
        async loadSiteContext() {
            try {
                let sites = this.$store.state.sites;
                if (!sites || sites.length === 0) {
                    const res = await client.robustQuery('/api', { slim: true });
                    const pid = this.$route.params.projectId || this.$store.state.project?.objectId;
                    sites = sitesUserCanAccess(res.sites || [], this.$store.state.user, pid);
                    this.$store.commit('setSites', sites);
                }
                const leftover = this.$store.state.site;
                const accessible = this.$store.getters.accessibleSites || [];
                const leftoverId = leftover ? (leftover.objectId || leftover.id) : null;
                if (leftover && isAccessibleSiteId(leftoverId, accessible)) {
                    this.site = leftover;
                    return;
                }
                const first = this.$store.getters.firstAccessibleSite || firstAccessibleSite(sites, this.$store.state.user, this.$route.params.projectId);
                if (first) {
                    this.site = {
                        objectId: first.objectId,
                        name: first.name,
                        commanderLicenseId: first.commanderLicenseId || null,
                        project: first.project || null,
                        canBeScheduled: first.canBeScheduled || false
                    };
                }
            } catch (error) {
                console.error('Failed to load site context:', error);
            }
        },
        
        async handleSearch(filters) {
            // Clear existing debounce timer
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }
            
            // Debounce rapid filter changes
            this.searchDebounceTimer = setTimeout(async () => {
                this.loadingSearch = true;
                this.currentFilters = { ...filters };
                await this.fetchPoints();
                this.loadingSearch = false;
            }, 300);
        },
        
        async handleTableChange(tableConfig) {
            // Update local state for tracking purposes
            this.pagination.page = tableConfig.page;
            this.pagination.limit = tableConfig.itemsPerPage === 'ALL' ? -1 : tableConfig.itemsPerPage;
            this.sortBy = tableConfig.sortBy;
            this.sortDesc = tableConfig.sortDesc;
        },
        
        async fetchPoints() {
            try {
                this.loading = true;
                
                // Build query parameters (exclude alarmStatus - we'll filter client-side)
                const params = {
                    limit: 10000
                };
                
                // Add non-empty filters (EXCEPT alarmStatus)
                Object.keys(this.currentFilters).forEach(key => {
                    if (key !== 'alarmStatus' && this.currentFilters[key] !== null && this.currentFilters[key] !== '') {
                        params[key] = this.currentFilters[key];
                    }
                });
                
                // Get all points without alarm status filtering
                const result = await client.robustQuery('/api/querypoints/points', params);
                this.allPoints = result.results || [];
                
                // Load alarm status data for these points
                await this.loadAlarmStatusForPoints();
                
                // Apply alarm status to points and filter
                this.applyAlarmStatusAndFilter();
                
            } catch (error) {
                console.error('Failed to fetch points:', error);
                // window.M.toast({ html: 'Failed to load points. Please try again.' });
                this.allPoints = [];
                this.filteredPoints = [];
            } finally {
                this.loading = false;
            }
        },
        
        async loadAlarmStatusForPoints() {
            if (this.allPoints.length === 0) {
                this.pointToMeasurementMap = {};
                this.alarmStatusLoaded = true;
                return;
            }
            
            this.alarmStatusLoading = true;
            
            try {
                // Determine which measurements to fetch based on current filters
                const measurementTargets = this.determineMeasurementTargets();
                
                // Load measurements for all targets in parallel
                const measurementsPromises = measurementTargets.map(async (target) => {
                    const result = await client.robustQuery('/api/measurements', {
                        siteId: target.siteId,
                        zoneId: target.zoneId
                    });
                    return { 
                        siteId: target.siteId, 
                        zoneId: target.zoneId, 
                        measurements: result.results || [] 
                    };
                });
                
                const allMeasurements = await Promise.all(measurementsPromises);
                
                // Build lookup table
                this.buildPointToMeasurementMap(allMeasurements);
                
                this.alarmStatusLoaded = true;
            } catch (error) {
                console.error('Failed to load alarm status:', error);
                // Continue with empty alarm status map
                this.pointToMeasurementMap = {};
                this.alarmStatusLoaded = true;
            } finally {
                this.alarmStatusLoading = false;
            }
        },
        
        determineMeasurementTargets() {
            const targets = [];
            
            // Case 1: Both site and zone filters are set
            if (this.currentFilters.siteId && this.currentFilters.zoneId) {
                targets.push({
                    siteId: this.currentFilters.siteId,
                    zoneId: this.currentFilters.zoneId
                });
            }
            // Case 2: Only site filter is set - get all zones for that site
            else if (this.currentFilters.siteId && !this.currentFilters.zoneId) {
                // Get unique zones for the filtered site from the points
                const uniqueZones = [...new Set(
                    this.allPoints
                        .filter(point => point.site && point.site.objectId === this.currentFilters.siteId)
                        .map(point => point.zone?.objectId)
                        .filter(Boolean)
                )];
                
                uniqueZones.forEach(zoneId => {
                    targets.push({
                        siteId: this.currentFilters.siteId,
                        zoneId: zoneId
                    });
                });
            }
            // Case 3: No site/zone filters - get all unique site/zone combinations from points
            else {
                const uniqueCombinations = [...new Set(
                    this.allPoints
                        .filter(point => point.site && point.zone)
                        .map(point => `${point.site.objectId}:${point.zone.objectId}`)
                )];
                
                uniqueCombinations.forEach(combination => {
                    const [siteId, zoneId] = combination.split(':');
                    targets.push({ siteId, zoneId });
                });
            }
            
            return targets;
        },
        
        buildPointToMeasurementMap(allMeasurements) {
            // Clear existing data
            this.pointToMeasurementMap = {};
            
            allMeasurements.forEach(({ siteId, zoneId, measurements }) => {
                measurements.forEach(measurement => {
                    if (measurement.childPoints && Array.isArray(measurement.childPoints)) {
                        measurement.childPoints.forEach(childPoint => {
                            this.pointToMeasurementMap[childPoint.objectId] = {
                                measurementId: measurement.objectId,
                                alerts: measurement.alerts || [],
                                isAlerting: measurement.alerts && measurement.alerts.length > 0
                            };
                        });
                    }
                });
            });
        },
        
        applyAlarmStatusAndFilter() {
            // Handle case where alarm status loading failed
            if (!this.alarmStatusLoaded && !this.alarmStatusLoading) {
                this.handleAlarmStatusError();
                return;
            }
            
            // Handle case where no points were loaded
            if (this.allPoints.length === 0) {
                this.filteredPoints = [];
                this.updatePagination();
                return;
            }
            
            // First, add alarm status to all points
            const pointsWithAlarmStatus = this.allPoints.map(point => {
                const measurementData = this.pointToMeasurementMap[point.objectId];
                return {
                    ...point,
                    alarmStatus: measurementData?.isAlerting || false,
                    // Add additional alarm info for debugging/display
                    measurementId: measurementData?.measurementId || null,
                    alertCount: measurementData?.alerts?.length || 0
                };
            });
            
            // Then apply alarm status filter if it's set
            if (this.currentFilters.alarmStatus !== null && this.currentFilters.alarmStatus !== '') {
                if (this.currentFilters.alarmStatus === 'active') {
                    // Show only points that are alerting
                    this.filteredPoints = pointsWithAlarmStatus.filter(point => point.alarmStatus === true);
                } else if (this.currentFilters.alarmStatus === 'not_active') {
                    // Show only points that are not alerting
                    this.filteredPoints = pointsWithAlarmStatus.filter(point => point.alarmStatus === false);
                } else {
                    // Unknown filter value - show all points
                    this.filteredPoints = pointsWithAlarmStatus;
                }
            } else {
                // No alarm status filter - show all points
                this.filteredPoints = pointsWithAlarmStatus;
            }
            
            // Validate the final filtered points
            if (!this.validatePointData(this.filteredPoints)) {
                console.error('Data validation failed for filtered points');
                // Fall back to showing all points without alarm status
                this.filteredPoints = this.allPoints.map(point => ({
                    ...point,
                    alarmStatus: false
                }));
            }
            
            // Update pagination for the filtered results
            this.updatePagination();
            
            // Debug logging
            if (process.env.NODE_ENV === 'development') {
                const activeCount = this.filteredPoints.filter(p => p.alarmStatus).length;
                const totalCount = this.filteredPoints.length;
                console.log(`Alarm status applied: ${activeCount} active, ${totalCount - activeCount} inactive out of ${totalCount} total points`);
            }
            
            // Log summary for debugging
            console.log(`Applied alarm status filtering: ${this.filteredPoints.length}/${this.allPoints.length} points shown`);
            
            // Show success message if alarm status filter is active
            if (this.currentFilters.alarmStatus && this.filteredPoints.length > 0) {
                const filterText = this.currentFilters.alarmStatus === 'active' ? 'active alarms' : 'inactive alarms';
                // window.M.toast({ html: `Showing ${this.filteredPoints.length} points with ${filterText}`, classes: 'success' });
            }
        },
        
        updatePagination() {
            const totalCount = this.filteredPoints.length; // Use filtered points, not sorted
            const limit = this.pagination.limit || 100;
            const totalPages = Math.ceil(totalCount / limit);
            
            this.pagination = {
                ...this.pagination,
                totalCount: totalCount,
                totalPages: totalPages,
                page: Math.min(this.pagination.page || 1, totalPages || 1)
            };
        },
        
        validatePointData(points) {
            return points.every(point => {
                // Ensure required fields exist
                if (!point.objectId) {
                    console.warn('Point missing objectId:', point);
                    return false;
                }
                
                // Ensure alarmStatus is boolean
                if (typeof point.alarmStatus !== 'boolean') {
                    console.warn('Point alarmStatus is not boolean:', point.objectId, point.alarmStatus);
                    return false;
                }
                
                return true;
            });
        },
        
        handleAlarmStatusError() {
            // If alarm status loading fails, still show points but without alarm status filtering
            console.warn('Alarm status loading failed, showing all points without alarm status filtering');
            
            // Add alarmStatus: false to all points as fallback
            this.filteredPoints = this.allPoints.map(point => ({
                ...point,
                alarmStatus: false,
                measurementId: null,
                alertCount: 0
            }));
            
            this.updatePagination();
            
            // Show user-friendly message
            // window.M.toast({ html: 'Alarm status information unavailable. Showing all points.', classes: 'warning' });
        },
        
        handleQuerySaved() {
            // Refresh saved queries in the filters component
            // This will be handled by the QueryFilters component
            // window.M.toast({ html: 'Query saved successfully!' });
        },
        updateChartColor(measurement) {
            if (measurement && measurement.chartData) {
                // Always use the default color for point explorer detail modal
                measurement.chartData.colorMode = '#4e7cff';
            }
        },
        async handlePointClick(point) {
            try {
                this.loading = true;
                // Fetch measurement data for the point to populate the modal
                const measurementData = await this.fetchMeasurementDataForPoint(point);
                console.log('---------------clicked-----------------');
                console.log('point', point);
                console.log('measurementData', measurementData);
                // Set the chart color before opening the modal
                this.updateChartColor(measurementData);
                this.selectedMeasurement = measurementData;
                this.showDetailModal = true;
            } catch (error) {
                console.error('Failed to load point details:', error);
                // window.M.toast({ html: 'Failed to load point details. Please try again.' });
            } finally {
                this.loading = false;
            }
        },
        
        async fetchMeasurementDataForPoint(point) {
            // This method will create a measurement-like object from point data
            // and fetch trend data to populate the DashboardAqDetailModal
            // Using the same strategy as DashboardAQMeasurement.vue
            
            try {
                // Calculate time range (same logic as DashboardAQMeasurement.vue)
                const startTime = moment().startOf('week').valueOf();
                const endTime = null; // Use null for current time, same as DashboardAQMeasurement.vue
                
                // Use store dispatch for trend data (same as DashboardAQMeasurement.vue)
                const chartData = await this.$store.dispatch('getPointTrendData', {
                    siteId: point.site.objectId,
                    pointId: point.objectId,
                    version: 'highcharts',
                    startTime: startTime,
                    endTime: endTime,
                    type: point.type,
                    force: false
                });
                
                // Create measurement-like object for the modal
                const measurementData = {
                    name: point.name,
                    type: point.type,
                    unit: point.unit,
                    curVal: point.curVal,
                    curScore: null, // Points don't have scores
                    chartData: null,
                    ready: false
                };
                
                // Process chartData same way as DashboardAQMeasurement.vue
                if (chartData && chartData.data && chartData.data.length > 0) {
                    measurementData.chartData = chartData;
                    
                    // Handle boolean points with activeText/inactiveText
                    if (point.activeText && point.inactiveText) {
                        measurementData.chartData.enumeration = {
                            activeText: point.activeText,
                            inactiveText: point.inactiveText
                        };
                        
                        if (point.kind === "Bool") {
                            if (!isNaN(chartData.data[0][1])) {
                                measurementData.chartData.data = chartData.data.map((el) => {
                                    return [el[0], el[1] ? point.activeText : point.inactiveText];
                                });
                            }
                        }
                    }
                    
                    // Handle multiStateValues
                    if (point.multiStateValues) {
                        measurementData.chartData.multiStateValues = point.multiStateValues;
                    }
                    
                    // Add timestamp for chart key
                    measurementData.chartData.lastUpdate = Date.now();
                } else {
                    // Set default chart data if API call failed (same as DashboardAQMeasurement.vue)
                    let themes = this.$store.state.themes || {};
                    measurementData.chartData = {
                        data: [],
                        colorMode: String(themes.ex || "#4e7cff"),
                        range: [0, 100],
                        lastUpdate: Date.now()
                    };
                }
                
                measurementData.ready = true;
                return measurementData;
                
            } catch (error) {
                console.error('Failed to fetch measurement data for point:', error);
                // Return basic measurement data without chart (same fallback structure)
                let themes = this.$store.state.themes || {};
                return {
                    name: point.name,
                    type: point.type,
                    unit: point.unit,
                    curVal: point.curVal,
                    curScore: null,
                    chartData: {
                        data: [],
                        colorMode: String(themes.ex || "#4e7cff"),
                        range: [0, 100],
                        lastUpdate: Date.now()
                    },
                    ready: true
                };
            }
        },
        
        handleSiteClick(site) {
            // Navigate to site monitoring page
            const projectId = this.$route.params.projectId || this.$store.state.project?.objectId;
            if (projectId && site.objectId) {
                this.$router.push({
                    name: 'Site',
                    params: {
                        projectId: projectId,
                        siteId: site.objectId
                    }
                });
            }
        },
        
        handleZoneClick(zone) {
            // Navigate to zone monitoring page
            const projectId = this.$route.params.projectId || this.$store.state.project?.objectId;
            if (projectId && zone.objectId) {
                // We need to find the site that contains this zone
                // For now, we'll use the current site context
                const siteId = this.site?.objectId || this.navbarSiteId;
                if (siteId) {
                    this.$router.push({
                        name: 'Zone',
                        params: {
                            projectId: projectId,
                            siteId: siteId,
                            zoneId: zone.objectId
                        }
                    });
                }
            }
        },
        
        closeDetailModal() {
            this.showDetailModal = false;
            this.selectedMeasurement = null;
        }
    },
    async mounted() {
        // Load site context for navbar
        await this.loadSiteContext();
        
        // Don't automatically fetch points - wait for search button click
    },
    watch: {
        '$route'() {
            // Handle route changes if needed
            // For now, just reload site context
            this.loadSiteContext();
        },
        
        // When filters change, we need to reload everything
        currentFilters: {
            handler() {
                // Only reload if this wasn't triggered by our own search
                if (this.loadingSearch) return;
                
                // Clear existing data
                this.alarmStatusLoaded = false;
                this.pointToMeasurementMap = {};
                this.allPoints = [];
                this.filteredPoints = [];
            },
            deep: true
        }
    }
};
</script>

<style lang="sass" scoped>
#pointExplorerPage
    min-height: 100vh
    background: var(--app-primary-bg)

    .top-header
        margin-top: 16px
        margin-bottom: 0
        .site-info
            .site-title
                color: var(--app-primary-txt)
                font-size: 28px

    .v-container
        max-width: 1200px
</style> 